import { useEffect, useState } from "react";
import { HelpCircle, RotateCcw } from "@/lib/icons";
import { Button } from "@/components/ui/button";
import HowToPlayDialog from "./HowToPlayDialog";
import { HOW_TO, isOnboarded, markOnboarded } from "./howTo";

/**
 * Standard per-game header strip: "How to play" (always shown) and an
 * optional "Reset" button. Auto-opens the how-to dialog the first time
 * a visitor lands on a given game (gated by `localStorage` per gameKey).
 *
 * Drop into a game page like:
 *   <GameMenu gameKey="ph-sort" onReset={resetMatch} />
 *
 * If `onReset` is omitted, the Reset button is hidden - useful for the
 * top-level mode-select screens that have no in-progress state to reset.
 */
export default function GameMenu({
  gameKey,
  onReset,
  resetLabel = "Reset",
  className,
}: {
  gameKey: string;
  onReset?: () => void;
  resetLabel?: string;
  className?: string;
}) {
  const content = HOW_TO[gameKey];
  const [open, setOpen] = useState(false);

  // Auto-open once per device per game.
  useEffect(() => {
    if (!content) return;
    if (!isOnboarded(content.gameKey)) {
      setOpen(true);
      markOnboarded(content.gameKey);
    }
  }, [content]);

  if (!content) {
    // Defensive: in dev, missing gameKey shouldn't crash the page.
    if (typeof console !== "undefined") {
      console.warn(`[GameMenu] No HOW_TO entry for "${gameKey}"`);
    }
    return null;
  }

  const handleReset = () => {
    if (!onReset) return;
    if (
      typeof window !== "undefined" &&
      !window.confirm("Reset this match? Scores will return to zero.")
    ) {
      return;
    }
    onReset();
  };

  return (
    <div className={`flex items-center gap-1 flex-wrap ${className ?? ""}`}>
      <Button
        variant="ghost"
        size="sm"
        onClick={() => setOpen(true)}
        className="text-xs h-8"
      >
        <HelpCircle size={12} className="mr-1" aria-hidden="true" />
        How to play
      </Button>
      {onReset && (
        <Button
          variant="ghost"
          size="sm"
          onClick={handleReset}
          className="text-xs h-8 text-muted-foreground"
        >
          <RotateCcw size={12} className="mr-1" aria-hidden="true" />
          {resetLabel}
        </Button>
      )}
      <HowToPlayDialog open={open} onOpenChange={setOpen} content={content} />
    </div>
  );
}
